// WebSocket hook for live server-sent events.
//
// Connects to the engine WS endpoint through the reverse proxy at
//   /api/vps/<id>/proxy/ws
//
// Dispatches a callback on every parsed WsEvent payload so container
// components can setState without coupling to the WS lifecycle.
//
// Auto-reconnects on close with a 5s backoff. Exposes connectionState
// so callers can show a "disconnected" badge.

import { useCallback, useEffect, useRef, useState } from "react";
import { getActiveVpsId } from "./client";
import type {
  AlertSeverity,
  PortfolioRiskSnapshot,
  SleeveAlert,
  TreasuryYields,
} from "./types";

export type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

export interface WsEvent {
  tag: string;
  // The rest of the fields depend on `tag`. We parse them as opaque
  // JSON and let the callback extract what it needs.
  [key: string]: unknown;
}

export interface LiveAlerts {
  critical: SleeveAlert[];
  warnings: SleeveAlert[];
  timestamp: number;
}

export interface LiveRiskSnapshot {
  snapshot: PortfolioRiskSnapshot;
  timestamp: number;
}

interface UseWsEventOpts {
  /** Called when a HealthAlert event arrives. */
  onHealthAlert?: (alerts: LiveAlerts) => void;
  /** Called when a PortfolioRiskSnapshot event arrives. */
  onRiskSnapshot?: (data: LiveRiskSnapshot) => void;
  /** Called when MarketDataUpdate event arrives (VIX, VXN, DXY, yields). */
  onMarketDataUpdate?: (data: { vix: number; vxn: number; dxy: number; yields: TreasuryYields; vix_gate_blocked: boolean }) => void;
  /** Called on connection state change. */
  onConnectionChange?: (state: ConnectionState) => void;
}

const RECONNECT_DELAY_MS = 5000;
const MAX_RECONNECTS = 3;

export function useWsEvent(opts: UseWsEventOpts) {
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectCount = useRef(0);

  const connect = useCallback(() => {
    const vpsId = getActiveVpsId();
    if (vpsId === null) return;

    if (reconnectCount.current >= MAX_RECONNECTS) {
      // Connection consistently fails — stop retrying
      return;
    }

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    const url = `${proto}//${host}/api/vps/${vpsId}/proxy/ws`;

    setConnectionState("connecting");
    opts.onConnectionChange?.("connecting");

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      reconnectCount.current += 1;
      reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
      return;
    }

    ws.onopen = () => {
      setConnectionState("connected");
      opts.onConnectionChange?.("connected");
      reconnectCount.current = 0; // Reset count on successful connection
    };

    ws.onmessage = (ev: MessageEvent) => {
      try {
        // The server sends one JSON-encoded WsEvent per message.
        // WsEvent is serialized via serde with tag="kind" and
        // rename_all="snake_case", e.g.:
        //   {"kind":"health_alert","alerts":[...]}
        //   {"kind":"portfolio_risk_snapshot","snapshot":{...}}
        //   {"kind":"market_data_update","vix":16.2,"dxy":104.3,"yields":{...}}
        const raw = JSON.parse(ev.data as string);

        if ((raw.kind === "health_alert" || raw.tag === "health_alert") && opts.onHealthAlert) {
          const alerts: SleeveAlert[] = raw.alerts ?? [];
          opts.onHealthAlert({
            critical: alerts.filter((a) => a.severity === ("critical" as AlertSeverity)),
            warnings: alerts.filter(
              (a) => a.severity === ("warning" as AlertSeverity) || a.severity === ("info" as AlertSeverity)
            ),
            timestamp: Date.now(),
          });
        }

        if ((raw.kind === "portfolio_risk_snapshot" || raw.tag === "portfolio_risk_snapshot") && opts.onRiskSnapshot) {
          opts.onRiskSnapshot({
            snapshot: raw.snapshot as PortfolioRiskSnapshot,
            timestamp: Date.now(),
          });
        }

        if ((raw.kind === "market_data_update" || raw.tag === "market_data_update") && opts.onMarketDataUpdate) {
          opts.onMarketDataUpdate({
            vix: raw.vix as number,
            vxn: raw.vxn as number,
            dxy: raw.dxy as number,
            yields: raw.yields as TreasuryYields,
            vix_gate_blocked: raw.vix_gate_blocked as boolean,
          });
        }
      } catch {
        // Malformed message — skip.
      }
    };

    ws.onerror = () => {
      setConnectionState("error");
      opts.onConnectionChange?.("error");
    };

    ws.onclose = () => {
      setConnectionState("disconnected");
      opts.onConnectionChange?.("disconnected");
      wsRef.current = null;
      reconnectCount.current += 1;
      // Auto-reconnect after delay.
      reconnectTimer.current = setTimeout(connect, RECONNECT_DELAY_MS);
    };

    wsRef.current = ws;
  }, [opts.onHealthAlert, opts.onRiskSnapshot, opts.onMarketDataUpdate, opts.onConnectionChange]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect]);

  return { connectionState };
}